import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Repeat, ShoppingBag, CheckCircle2, ArrowRight, Loader2, History, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useCurrency } from "../../lib/currency";
import { fmtDateTime } from "../../lib/ist";

const COST_TABLE = [
  { group: "Website", rows: [["Small change", 1], ["Big change", 5], ["Generate new site", 5]] },
  { group: "Logo", rows: [["Simple", 1], ["Medium", 5], ["Complex (icon + wordmark + brand kit)", 15]] },
  { group: "Image", rows: [["Simple", 1], ["Medium", 5], ["Complex", 15]] },
  { group: "Video", rows: [["Simple (5s)", 50], ["Medium (10s)", 100], ["Complex (30s)", 300]] },
];

export default function AICredits() {
  const { addToCart } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [w, l, p] = await Promise.all([
        api.get("/credits/me").then(r => r.data),
        api.get("/credits/ledger?limit=50").then(r => r.data),
        api.get("/credits/plans").then(r => r.data),
      ]);
      setWallet(w); setLedger(l); setPlans(p);
    } catch { toast.error("Failed to load wallet"); }
  };
  useEffect(() => { load(); }, []);

  const subs = plans.filter(p => p.billing_cycle === "monthly");
  const topups = plans.filter(p => p.billing_cycle === "one_time");

  // All credit purchases now go through the cart → admin-approval flow.
  // Direct subscription/checkout bypasses have been removed so every order
  // is reviewed by the team before payment is collected.
  const addAndGo = (plan) => {
    setBusy(plan.sku);
    try {
      addToCart({ sku: plan.sku, name: plan.name, price: plan.price });
      toast.success(`${plan.name} added to cart`);
      nav("/checkout");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="ai-credits-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tag mb-2">AI Wallet</div>
          <h1 className="text-3xl font-extrabold text-white" style={{ fontFamily: "Outfit" }}>AI Credits</h1>
          <p className="text-slate-400 text-sm">Use credits across the Website Builder, Logo &amp; Image generation and Video creation.</p>
        </div>
      </div>

      {/* Balance card */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card p-6 lg:col-span-2 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 opacity-20 blur-3xl" />
          <div className="flex items-center gap-2 text-blue-400"><Wallet size={16} /><span className="text-xs uppercase tracking-wider">Current balance</span></div>
          <div className="mt-2 flex items-end gap-3">
            <span className="text-6xl font-black text-white" style={{ fontFamily: "Outfit" }} data-testid="wallet-balance">{wallet ? wallet.balance : "—"}</span>
            <span className="text-slate-400 text-sm pb-2">credits</span>
          </div>
          <div className="mt-3 flex gap-4 text-xs text-slate-400">
            <span>Total purchased: <span className="text-white font-semibold">{wallet?.total_purchased ?? 0}</span></span>
            <span>·</span>
            <span>Total used: <span className="text-white font-semibold">{wallet?.total_used ?? 0}</span></span>
          </div>
        </div>
        <div className="card p-5" data-testid="cost-rules-card">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Sparkles size={15} className="text-blue-400" /> Credit cost guide</h3>
          <div className="space-y-2.5 text-xs">
            {COST_TABLE.map(g => (
              <div key={g.group}>
                <div className="text-cyan-300 font-semibold mb-1">{g.group}</div>
                <ul className="space-y-0.5">
                  {g.rows.map(r => (
                    <li key={r[0]} className="flex items-center justify-between text-slate-300">
                      <span>{r[0]}</span>
                      <span className="text-white font-mono">{r[1]} cr</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly plans */}
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Repeat size={18} className="text-blue-400" /> Monthly Plans</h2>
        <p className="text-slate-400 text-sm">Auto-refill every month. Cancel anytime from Subscriptions.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {subs.map(p => (
            <PlanCard key={p.sku} plan={p} format={format} busy={busy} onPick={() => addAndGo(p)} cta="Add to cart" testid={`plan-${p.sku}`} />
          ))}
        </div>
      </div>

      {/* Top-up packs */}
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ShoppingBag size={18} className="text-blue-400" /> One-time Top-ups</h2>
        <p className="text-slate-400 text-sm">No subscription. Credits never expire and stack on top of your monthly plan.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {topups.map(p => (
            <PlanCard key={p.sku} plan={p} format={format} busy={busy} onPick={() => addAndGo(p)} cta="Add to cart" testid={`topup-${p.sku}`} />
          ))}
        </div>
      </div>

      {/* Ledger */}
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}><History size={18} className="text-blue-400" /> Recent activity</h2>
        <div className="card overflow-x-auto" data-testid="ledger-table">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-3 px-4 text-left">When</th>
                <th className="text-left">Type</th>
                <th className="text-left">Reason</th>
                <th className="text-right">Δ</th>
                <th className="text-right pr-4">Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map(e => (
                <tr key={e.id} className="border-b border-slate-800">
                  <td className="py-2.5 px-4 text-slate-400 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                  <td className="text-slate-300 whitespace-nowrap">{e.type.replace("_", " ")}</td>
                  <td className="text-slate-300">{e.reason}</td>
                  <td className={`text-right font-mono font-bold ${e.delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</td>
                  <td className="text-right pr-4 text-white font-mono">{e.balance_after}</td>
                </tr>
              ))}
              {ledger.length === 0 && (
                <tr><td colSpan="5" className="text-center py-10 text-slate-500">No activity yet — buy a plan or top-up above to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, format, busy, onPick, cta, testid }) {
  const isBusy = busy === plan.sku;
  return (
    <div className={`card p-5 relative ${plan.popular ? "ring-2 ring-blue-500/60" : ""}`} data-testid={testid}>
      {plan.popular && <div className="absolute -top-3 left-5 px-3 py-1 rounded-full bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider">Popular</div>}
      <div className="text-blue-400 text-xs uppercase tracking-wider">{plan.credits} credits {plan.billing_cycle === "monthly" ? "/ month" : ""}</div>
      <div className="text-2xl font-extrabold text-white mt-1" style={{ fontFamily: "Outfit" }}>{plan.name}</div>
      <div className="mt-2 text-3xl font-black text-white" style={{ fontFamily: "Outfit" }}>{format(plan.price)}<span className="text-sm font-normal text-slate-400 ml-1">{plan.billing_cycle === "monthly" ? "/ mo" : ""}</span></div>
      <p className="text-slate-400 text-xs mt-1 min-h-[36px]">{plan.description}</p>
      <ul className="mt-3 space-y-1 text-xs text-slate-300 min-h-[80px]">
        {(plan.features || []).slice(0, 4).map(f => (
          <li key={f} className="flex items-start gap-1.5"><CheckCircle2 size={13} className="text-emerald-400 shrink-0 mt-0.5" /><span>{f}</span></li>
        ))}
      </ul>
      <button
        onClick={() => onPick(plan)}
        disabled={isBusy}
        className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2"
        data-testid={`${testid}-btn`}
      >
        {isBusy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} {cta}
      </button>
    </div>
  );
}
